import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';
import { getItemUnitName, getSelectedMappingLines } from './helpers/item-display.helper';

/**
 * Default Thermal Template - POS Thermal Printer Size
 * Current ElixirPOS implementation (80mm thermal paper)
 */
export class ThermalVersionTemplate extends BasePdfTemplate {
  override templateName = 'thermal-version';
  override templateVersion = '1.0.0';
  override description =
    'Thermal Version template (80mm POS paper) with IGST support';
  override pageSize: 'THERMAL' = 'THERMAL';

  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    const storename = JSON.parse(localStorage.getItem('storename') || '{}');
    let storeAddress = '';

    if (storename) {
      const addressParts = [
        storename.address,
        storename.address1,
        storename.address2,
        storename.pincode,
      ].filter((part) => part && part.trim());
      storeAddress = addressParts.join(', ');
    }
    const businessName = storename?.name || data.businessName || '';
    const businessAddress = storeAddress || data.businessAddress || '';
    const businessPhone = storename?.mobile || data.businessPhone || '';

    const formatDate = this.formatDate.bind(this);
    const businessGSTIN = data.businessGSTIN || '';
    // ✅ Compute once — accessible everywhere below
    const showInclusive = !!data.discaftertax;
    // const tableItemsTotal = data.items.reduce((sum: number, item: any) => {
    //   const qty = item.quantity || 1;
    //   const taxPerUnit = (item.taxamount || 0) / qty;
    //   const inclusivePrice = item.price + taxPerUnit;
    //   return sum + (showInclusive ? inclusivePrice * qty : (item.total || 0));
    // }, 0);
    // Free items (is_free === 1) are excluded from the table total
    const tableItemsTotal = data.items.reduce((sum: number, item: any) => {
      if (Number(item.is_free) === 1) return sum;
      const qty = item.quantity || 1;
      const taxPerUnit = (item.taxamount || 0) / qty;
      const inclusivePrice = item.price + taxPerUnit;
      return sum + (showInclusive ? inclusivePrice * qty : (item.total || 0));
    }, 0);

    // Free items summary (is_free === 1): total free qty and free value (MRP/original price x qty)
    let totalFreeQty = 0;
    let totalFreeAmount = 0;
    (data.items || []).forEach((item: any) => {
      if (Number(item.is_free) === 1) {
        totalFreeQty += Number(item.quantity || 0);
        totalFreeAmount += Number(item.originalprice || item.mrp || 0) * Number(item.quantity || 0);
      }
    });

    // netAmount uses tableItemsTotal, which already excludes free items
    const netAmount = tableItemsTotal
      - (data.itemDiscount || 0)
      - (data.overallDiscount || 0)
      - (Number(data.loyaltyAmount) || 0);
    return {
      pageSize: this.getPageSize(),
      pageMargins: this.getPageMargins(),
      pageBreakBefore: function (currentNode, followingNodesOnPage) {
        return false;
      },
      content: [
        {
          text: businessName,
          fontSize: 14,
          bold: true,
          alignment: 'center',
        },
        {
          text: `${businessAddress},`,
          fontSize: 8,
          alignment: 'center',
        },
        {
          text: `Phone : ${businessPhone}`,
          fontSize: 9,
          alignment: 'center',
        },
        {
          text: `GST: ${businessGSTIN || '-'}`,
          fontSize: 10,
          alignment: 'center',
        },
        // {
        //   text: `FSSAI:`,
        //   fontSize: 10,
        //   alignment: 'center',
        // },
        {
          text: 'Retail Invoice',
          fontSize: 10,
          alignment: 'center',
          bold: true,
          decoration: 'underline',
          margin: [0, 5, 0, 5],
        },
        // ===== ORDER INFO =====
        {
          layout: 'noBorders',
          table: {
            body: [
              [
                { text: 'Bill', bold: true, fontSize: 9 },
                { text: `: ${data.invoiceNumber}`, fontSize: 9 },
              ],
              // [
              //   { text: 'Bill To', bold: true, fontSize: 9 },
              //   { text: `: ${data.customerMobile}`, fontSize: 9 },
              // ],
              [
                { text: 'Date', bold: true, fontSize: 9 },
                {
                  text: `: ${formatDate((data.invoiceDate || data.invoicedate || '') as string | Date)}`,
                  fontSize: 9,
                },
              ],
            ],
          },
        },

        // ===== ITEM TABLE =====
        {
          margin: [0, 10, 0, 10],
          layout: {
            hLineWidth: function (i: any, node: any) {
              // draw horizontal lines for header & totals only
              return i === 1 || i === node.table.body.length ? 0.5 : 0.5;
            },
            vLineWidth: function () {
              return 0;
            },
            hLineColor: function () {
              return '#000000';
            },
            paddingTop: function () {
              return 2;
            },
            paddingBottom: function () {
              return 2;
            },
          },
          table: {
            widths: [40, 20, 'auto', 'auto', '*'], // fits 80mm paper
            dontBreakRows: true,
            keepWithHeaderRows: 1,
            body: [
              // ==== Header Row ====
              [
                {
                  text: 'Item',
                  alignment: 'left',
                  bold: true,
                  border: [false, true, false, true],
                  fontSize: 10,
                },
                {
                  text: 'MRP',
                  alignment: 'center',
                  bold: true,
                  border: [false, true, false, true],
                  fontSize: 10,
                },
                {
                  text: 'Qty',
                  alignment: 'center',
                  bold: true,
                  border: [false, true, false, true],
                  fontSize: 10,
                },
                {
                  text: 'Rate',
                  alignment: 'center',
                  bold: true,
                  border: [false, true, false, true],
                  fontSize: 10,
                },
                {
                  text: 'Amount',
                  alignment: 'center',
                  bold: true,
                  border: [false, true, false, true],
                  fontSize: 10,
                },
              ],

              // ==== Item Rows ====
              // ...data.items.map((item: any) => {
              //   const taxableAmount = item.total || 0;
              //   const taxAmount = item.taxamount || 0;
              //   const itemAmount = taxableAmount + taxAmount;

              //   return [
              //     {
              //       text: item.name,
              //       alignment: 'left',
              //       border: [false, false, false, false],
              //       fontSize: 9,
              //     },
              //     {
              //       text: item.mrp.toFixed(2),
              //       alignment: 'right',
              //       border: [false, false, false, false],
              //       fontSize: 9,
              //     },
              //     {
              //       text: item.quantity,
              //       alignment: 'center',
              //       border: [false, false, false, false],
              //       fontSize: 9,
              //     },
              //     {
              //       text: this.orgpricewithtax(item),
              //       alignment: 'right',
              //       border: [false, false, false, false],
              //       fontSize: 9,
              //     },
              //     {
              //       text: this.getAmtInclTax(item),
              //       alignment: 'right',
              //       border: [false, false, false, false],
              //       fontSize: 9,
              //     },
              //   ];
              // }),
              // ...data.items.map((item: any) => {
              // flatMap so an item can add an extra full-width row for its selected mapping lines
              ...data.items.flatMap((item: any) => {
                const qty = item.quantity || 1;
                const taxPerUnit = (item.taxamount || 0) / qty;
                const inclusivePrice = item.price + taxPerUnit;
                const unitName = getItemUnitName(item);
                const mappingLines = getSelectedMappingLines(item);

                // return [
                const itemRow: any[] = [
                  {
                    text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`,
                    alignment: 'left',
                    border: [false, false, false, false],
                    fontSize: 9,
                  },
                  {
                    text: item.mrp.toFixed(2),
                    alignment: 'right',
                    border: [false, false, false, false],
                    fontSize: 9,
                  },
                  {
                    // text: item.quantity,
                    // Qty column is 'auto' width, so the unit is shown next to the qty
                    text: unitName ? `${item.quantity} ${unitName}` : item.quantity,
                    alignment: 'center',
                    border: [false, false, false, false],
                    fontSize: 9,
                  },
                  {
                    // Rate column: show inclusive rate only when discAfterTax
                    text: showInclusive
                      ? this.orgpricewithtax(item)
                      : (item.price ?? item.originalprice ?? 0).toFixed(2),
                    alignment: 'right',
                    border: [false, false, false, false],
                    fontSize: 9,
                  },
                  {
                    // Amount column: always show incl-tax total when discAfterTax
                    text: Number(item.is_free) === 1
                      ? '0.00'
                      : showInclusive
                      ? (inclusivePrice * qty).toFixed(2)   // ✅ consistent with tableItemsTotal
                      : (item.total || 0).toFixed(2),
                    alignment: 'right',
                    border: [false, false, false, false],
                    fontSize: 9,
                  },
                ];

                if (!mappingLines.length) return [itemRow];

                // Selected variants / parameters under the item name — one per line, full-width row
                // (Item column is only 40pt wide); 5 cells keep the column count valid
                const mappingRow: any[] = [
                  {
                    stack: mappingLines.map((line: string) => ({ text: line, fontSize: 7 })),
                    colSpan: 5,
                    alignment: 'left',
                    border: [false, false, false, false],
                  },
                  {},
                  {},
                  {},
                  {},
                ];
                return [itemRow, mappingRow];
              }),

              // ==== Subtotal Row ====
              [
                {
                  text: `Item : ${data.items.length}`,
                  bold: true,
                  fontSize: 8,
                  border: [false, true, false, true],
                },
                {
                  text: `Qty : ${this.getTotalQuantity(data)}`,
                  bold: true,
                  colSpan: 1,
                  fontSize: 8,
                  border: [false, true, false, true],
                },
                {
                  text: 'Total',
                  bold: true,
                  fontSize: 8,
                  alignment: 'right',
                  border: [false, true, false, true],
                },
                {},
                {
                  text: tableItemsTotal.toFixed(2),
                  bold: true,
                  fontSize: 8,
                  alignment: 'right',
                  border: [false, true, false, true],
                },
              ],

              // ==== Free Items Row (only when free items exist) ====
              ...(totalFreeQty > 0
                ? [
                  [
                    {
                      text: `Free Items : ${totalFreeQty}`,
                      colSpan: 4,
                      bold: true,
                      fontSize: 8,
                      border: [false, true, false, true],
                    },
                    {},
                    {},
                    {},
                    {
                      text: totalFreeAmount.toFixed(2),
                      bold: true,
                      fontSize: 8,
                      alignment: 'right',
                      border: [false, true, false, true],
                    },
                  ],
                ]
                : []),

              // ==== Tax Section (IGST or CGST+SGST) ====
              // Show IGST for inter-state, CGST+SGST for intra-state
              // ...(this.isInterStateTax(data)
              //   ? [
              //       // Inter-state: Show IGST only
              //       [
              //         {
              //           text: 'IGST',
              //           colSpan: 4,
              //           bold: true,
              //           fontSize: 8,
              //           alignment: 'right',
              //           border: [false, true, false, true],
              //         },
              //         {},
              //         {},
              //         {},
              //         {
              //           text: `${this.getTotalIGST(data).toFixed(2)}`,
              //           bold: true,
              //           fontSize: 8,
              //           alignment: 'right',
              //           border: [false, true, false, true],
              //         },
              //       ],
              //     ]
              //   : [
              //       // Intra-state: Show CGST + SGST
              //       [
              //         {
              //           text: 'CGST',
              //           colSpan: 4,
              //           bold: true,
              //           fontSize: 8,
              //           alignment: 'right',
              //           border: [false, true, false, true],
              //         },
              //         {},
              //         {},
              //         {},
              //         {
              //           text: `${this.getTotalCGST(data).toFixed(2)}`,
              //           bold: true,
              //           fontSize: 8,
              //           alignment: 'right',
              //           border: [false, true, false, true],
              //         },
              //       ],
              //       [
              //         {
              //           text: 'SGST',
              //           colSpan: 4,
              //           bold: true,
              //           fontSize: 8,
              //           alignment: 'right',
              //           border: [false, true, false, true],
              //         },
              //         {},
              //         {},
              //         {},
              //         {
              //           text: `${this.getTotalSGST(data).toFixed(2)}`,
              //           bold: true,
              //           fontSize: 8,
              //           alignment: 'right',
              //           border: [false, true, false, true],
              //         },
              //       ],
              //     ]),

              // ==== Grand Total (before overall discount) ====
              [
                {
                  text: 'Grand Total',
                  colSpan: 4,
                  bold: true,
                  fontSize: 10,
                  alignment: 'right',
                  border: [false, true, false, true],
                },
                {},
                {},
                {},
                {
                  text: `${this.getTotalAmtInclTax(data).toFixed(2)}`,
                  //text: `${(data.totalAmount || data.grandTotal || 0).toFixed(2)}`,
                  bold: true,
                  fontSize: 10,
                  alignment: 'right',
                  border: [false, true, false, true],
                },
              ],
              // data.itemDiscount && data.itemDiscount > 0 Item Discount
              // Item Discount row (if present)
              ...(data.itemDiscount && data.itemDiscount > 0
                ? [
                  [
                    {
                      text: 'Item Discount',
                      colSpan: 4,
                      bold: true,
                      fontSize: 8,
                      alignment: 'right',
                      border: [false, true, false, true],
                    },
                    {},
                    {},
                    {},
                    {
                      text: `- ${data.itemDiscount.toFixed(2)}`,
                      bold: true,
                      fontSize: 8,
                      alignment: 'right',
                      border: [false, true, false, true],
                    },
                  ],
                ]
                : []),
              // ==== Overall Discount (conditional) ====
              ...(data.overallDiscount && data.overallDiscount > 0
                ? [
                  [
                    {
                      text: 'Overall Discount',
                      colSpan: 4,
                      bold: true,
                      fontSize: 8,
                      alignment: 'right',
                      border: [false, true, false, true],
                    },
                    {},
                    {},
                    {},
                    {
                      text: `- ${data.overallDiscount.toFixed(2)}`,
                      bold: true,
                      fontSize: 8,
                      alignment: 'right',
                      border: [false, true, false, true],
                    },
                  ],
                ]
                : []),

              ...((Number(data.loyaltyAmount) || 0) > 0
                ? [
                  [
                    {
                      text: `Loyalty Discount (${Number(data.loyaltyPoints) || 0} pts)`,
                      colSpan: 4,
                      bold: true,
                      fontSize: 8,
                      alignment: 'right',
                      border: [false, true, false, true],
                    },
                    {},
                    {},
                    {},
                    {
                      text: `- ${(Number(data.loyaltyAmount) || 0).toFixed(2)}`,
                      bold: true,
                      fontSize: 8,
                      alignment: 'right',
                      border: [false, true, false, true],
                    },
                  ],
                ]
                : []),

              ...((data.overallDiscount && data.overallDiscount > 0) ||
                (data.itemDiscount && data.itemDiscount > 0) ||
                (Number(data.loyaltyAmount) || 0) > 0
                ? [
                  [
                    {
                      text: 'Net Amount',
                      colSpan: 4,
                      bold: true,
                      fontSize: 10,
                      alignment: 'right',
                      border: [false, false, false, true],
                    },
                    {},
                    {},
                    {},
                    {
                      text: `${((data.totalAmount || data.grandTotal || 0) - (Number(data.loyaltyAmount) || 0)).toFixed(2)}`,
                      bold: true,
                      fontSize: 10,
                      alignment: 'right',
                      border: [false, false, false, true],
                    },
                  ],
                ]
                : []),
            ],
          },
        },
        {
          text: 'Inclusive of all GST tax',
          fontSize: 9,
          alignment: 'center',
        },
        {
          text: 'Thank you Visit again!',
          bold: true,
          fontSize: 12,
          alignment: 'center',
          margin: [0, 20, 0, 0],
        },
        {
          text: 'Return Within 7 Days',
          fontSize: 9,
          alignment: 'center',
        },
        {
          text: 'No Cash Refunded',
          fontSize: 9,
          alignment: 'center',
        },
        {
          text: '*********************************',
          fontSize: 9,
          alignment: 'center',
          margin: [0, 8, 0, 0],
        },
      ],
    };
  }

  // Calculate total quantity
  private getTotalQuantity(invoice: InvoiceData): number {
    console.log('thermal-version-template loaded');
    // return invoice.items.reduce(
    //   (sum: any, product: any) => sum + product.quantity,
    //   0,
    // );
    // return invoice.items.reduce(
    //   (sum: any, product: any) => sum + (Number(product.is_free) === 1 ? 0 : product.quantity),
    //   0,
    // );
    // T.Qty includes free qty; only amounts exclude free items
    return invoice.items.reduce(
      (sum: any, product: any) => sum + product.quantity,
      0,
    );
  }

  private getAmtInclTotal(invoice: InvoiceData): number {
    let grandtotal = invoice.items.reduce(
      (sum: any, product: any) =>
        sum + (Number(product.is_free) === 1 ? 0 : product.quantity * product.originalprice),
      0,
    );
    return Math.round(grandtotal) as any;
  }

  private orgpricewithtax(item: any): string {
    const price = item.originalprice;
    if (isNaN(price)) {
      return item.originalprice.toString(); // Return original string if parsing fails
    } else {
      let priceWithTax =
        (item.originalprice * item.taxRate) / 100 + item.originalprice;
      return Math.round(priceWithTax).toFixed(2); // Format to 2 decimal places
    }
  }

  private getAmtInclTax(item: any): string {
    const price = item.originalprice;
    if (isNaN(price)) {
      return item.originalprice.toString(); // Return original string if parsing fails
    } else {
      let priceWithTax =
        (item.originalprice * item.taxRate) / 100 + item.originalprice;
      let priceWithTaxandQty = priceWithTax * item.quantity;
      return Math.round(priceWithTaxandQty).toFixed(2); // Format to 2 decimal places
    }
  }
  private getTotalAmtInclTax(invoice: InvoiceData): number {
    if (!invoice?.items?.length) return 0;

    let grandTotal = invoice.items.reduce((sum: number, product: any) => {
      // Free items (is_free === 1) are excluded from the grand total
      if (Number(product.is_free) === 1) return sum;
      const price = Number(product.originalprice) || 0;
      const taxRate = Number(product.taxRate) || 0;
      const quantity = Number(product.quantity) || 0;

      const priceWithTax = price + (price * taxRate) / 100;

      return sum + priceWithTax * quantity;
    }, 0);

    return Math.round(grandTotal);
  }
  private getFinalTotal(invoice: InvoiceData): number {
    let grandtotal = invoice.items.reduce(
      (sum: any, product: any) => sum + (Number(product.is_free) === 1 ? 0 : product.total + product.taxamount),
      0,
    );
    return Math.round(grandtotal) as any;
  }
  // Calculate final amount (grand total - overall discount)
  private getGrandTotal(invoice: InvoiceData): number {
    return (
      (invoice.totalAmount || invoice.grandTotal || 0) -
      (invoice.overallDiscount || 0)
    );
  }
  private getTotalCGST(invoice: InvoiceData): number {
    return (
      invoice.cgst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          sum + (Number(product.is_free) === 1 ? 0 : (product.cgstval || product.cgstAmount || 0)),
        0,
      )
    );
  }
  // Calculate total SGST
  private getTotalSGST(invoice: InvoiceData): number {
    return (
      invoice.sgst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          sum + (Number(product.is_free) === 1 ? 0 : (product.sgstval || product.sgstAmount || 0)),
        0,
      )
    );
  }
  // Calculate total IGST
  private getTotalIGST(invoice: InvoiceData): number {
    return (
      invoice.igst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          sum + (Number(product.is_free) === 1 ? 0 : (product.igstval || product.igstAmount || 0)),
        0,
      )
    );
  }
  // Check if this is an inter-state transaction (IGST)
  private isInterStateTax(invoice: InvoiceData): boolean {
    const igst = this.getTotalIGST(invoice);
    const cgst = this.getTotalCGST(invoice);
    const sgst = this.getTotalSGST(invoice);

    // If IGST > 0 and CGST/SGST are 0, it's inter-state
    return igst > 0 && cgst === 0 && sgst === 0;
  }
}
